import { aptOf, bldgOf } from './listingId';
import { formatDMYDot } from './iso-date';

// DreamLand reception WhatsApp number (digits only). The operator forwards
// booking confirmations and cleaning/laundry orders to this chat.
export const RECEPTION_PHONE_DIGITS = '995577079977';

// WhatsApp order to DreamLand reception for one cleaning day. The message is
// always Russian (reception staff). Only the still-pending tasks are listed —
// a done task is dropped. The last line bills the turnover to the owner's
// account number.
export function buildCleaningOrderUrl(input: {
  listingId: number;
  cleaningDate: string;
  cleaningDone: boolean;
  linenDone: boolean;
}): string {
  const tasks: string[] = [];
  if (!input.cleaningDone) tasks.push('уборка');
  if (!input.linenDone) tasks.push('стирка обычная');
  const message = [
    `Апартамент ${bldgOf(input.listingId)}/${aptOf(input.listingId)}`,
    `Дата: ${formatDMYDot(input.cleaningDate)}`,
    `Нужно: ${tasks.join(', ')}`,
    'За счёт номера 14/1613',
  ].join('\n');
  return `https://wa.me/${RECEPTION_PHONE_DIGITS}?text=${encodeURIComponent(message)}`;
}

export type QuoteMessageInput = {
  apartmentTitle: string;
  listingId: number;
  checkin: string;
  checkout: string;
  guests: number;
  totalUsd: number;
  nights: number;
  tier: 'daily' | 'weekly';
  poolIncluded: boolean;
  locale: string;
};

export function formatQuoteMessage(q: QuoteMessageInput): string {
  const lines = [
    `Hello! I'd like to book:`,
    ``,
    `Apartment: ${q.apartmentTitle} (#${q.listingId})`,
    `Check-in: ${q.checkin}`,
    `Check-out: ${q.checkout}`,
    `Nights: ${q.nights}`,
    `Guests: ${q.guests}`,
    `Estimated total: $${q.totalUsd}`,
  ];
  if (q.poolIncluded) lines.push(`(pool access included)`);
  lines.push(``, `Sent from georgiadream.ge`);
  return lines.join('\n');
}

export function buildWhatsappUrl(phone: string, q: QuoteMessageInput): string {
  const digits = phone.replace(/\D/g, '');
  const text = encodeURIComponent(formatQuoteMessage(q));
  return `https://wa.me/${digits}?text=${text}`;
}

export function buildMailtoUrl(email: string, q: QuoteMessageInput): string {
  const subject = encodeURIComponent(`Booking inquiry — ${q.apartmentTitle}`);
  const body = encodeURIComponent(formatQuoteMessage(q));
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

export function buildTelegramUrl(telegramLink: string, q: QuoteMessageInput): string {
  if (!telegramLink) return '';
  const text = encodeURIComponent(formatQuoteMessage(q));
  const sep = telegramLink.includes('?') ? '&' : '?';
  return `${telegramLink}${sep}text=${text}`;
}

export type SimpleInquiryInput = {
  apartmentTitle: string;
  listingId: number | string;
  locale: string;
  checkin?: string;
  checkout?: string;
  guests?: number;
};

// Base inquiry sentence per locale; English is the fallback.
type SimpleInquiryBase = (apt: string, id: string) => string;

const simpleInquiryBaseEn: SimpleInquiryBase = (apt, id) =>
  `Hello! I'm interested in ${apt} (#${id}) on georgiadream.ge. Could you share availability and details?`;

const simpleInquiryBase: Record<string, SimpleInquiryBase> = {
  en: simpleInquiryBaseEn,
  ru: (apt, id) => `Здравствуйте! Интересует апартамент ${apt} (#${id}) на georgiadream.ge. Подскажите доступные даты и условия.`,
  ka: (apt, id) => `გამარჯობა! მაინტერესებს ბინა ${apt} (#${id}) საიტიდან georgiadream.ge. გთხოვთ, მომაწოდოთ თავისუფალი თარიღები და პირობები.`,
};

// Optional second line naming the picked dates + guests, per locale.
type SimpleDatesLine = (from: string, to: string, guests: number) => string;

const simpleDatesLine: Record<string, SimpleDatesLine> = {
  en: (from, to, g) => `Dates of interest: ${from}–${to}, guests: ${g}.`,
  ru: (from, to, g) => `Интересуют даты ${from}–${to}, гостей: ${g}.`,
  ka: (from, to, g) => `მაინტერესებს თარიღები ${from}–${to}, სტუმრები: ${g}.`,
};

// Generic inquiry text. Appends the dates/guests line only when BOTH endpoints
// are present (even if those dates are unavailable) — mirrors the widget's
// "smart dates" behaviour.
export function formatSimpleInquiry(q: SimpleInquiryInput): string {
  const base = (simpleInquiryBase[q.locale] ?? simpleInquiryBaseEn)(
    q.apartmentTitle,
    String(q.listingId),
  );
  if (q.checkin && q.checkout) {
    const line = (simpleDatesLine[q.locale] ?? simpleDatesLine.en)(
      formatDMYDot(q.checkin),
      formatDMYDot(q.checkout),
      q.guests ?? 1,
    );
    return `${base}\n${line}`;
  }
  return base;
}

export function buildSimpleWhatsappUrl(phone: string, q: SimpleInquiryInput): string {
  const digits = phone.replace(/\D/g, '');
  const text = encodeURIComponent(formatSimpleInquiry(q));
  return `https://wa.me/${digits}?text=${text}`;
}

export function buildSimpleMailtoUrl(email: string, q: SimpleInquiryInput): string {
  const subject = encodeURIComponent(`Booking inquiry — ${q.apartmentTitle}`);
  const body = encodeURIComponent(formatSimpleInquiry(q));
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

export function buildSimpleTelegramUrl(telegramLink: string, q: SimpleInquiryInput): string {
  if (!telegramLink) return '';
  const text = encodeURIComponent(formatSimpleInquiry(q));
  const sep = telegramLink.includes('?') ? '&' : '?';
  return `${telegramLink}${sep}text=${text}`;
}

// "Ask a question" low-commitment variant. Same deep-link mechanics as the
// simple inquiry, but a softer, open-ended tone that ends on a dash so the
// guest finishes the sentence with their actual question. The apartment is
// optional: when absent (e.g. the home hero) the text asks generically about
// the resort. Used by components/booking/AskQuestionCta.vue.
export type QuestionInput = {
  apartmentTitle?: string;
  listingId?: number | string;
  locale: string;
  checkin?: string;
  checkout?: string;
  guests?: number;
};

type QuestionBase = (apt: string, id: string) => string;

const questionBaseEn: QuestionBase = (apt, id) =>
  `Hello! I have a question about ${apt} (#${id}) on georgiadream.ge — `;

const questionBase: Record<string, QuestionBase> = {
  en: questionBaseEn,
  ru: (apt, id) => `Здравствуйте! У меня вопрос по апартаменту ${apt} (#${id}) на georgiadream.ge — `,
  ka: (apt, id) => `გამარჯობა! მაქვს კითხვა ბინასთან ${apt} (#${id}) დაკავშირებით (georgiadream.ge) — `,
};

// Generic (no apartment) question text per locale; English is the fallback.
const questionGenericEn = `Hello! I have a question about your apartments at Dreamland Oasis (georgiadream.ge) — `;

const questionGeneric: Record<string, string> = {
  en: questionGenericEn,
  ru: `Здравствуйте! У меня вопрос по апартаментам в Dreamland Oasis (georgiadream.ge) — `,
  ka: `გამარჯობა! მაქვს კითხვა Dreamland Oasis-ის ბინებთან დაკავშირებით (georgiadream.ge) — `,
};

export function formatQuestionMessage(q: QuestionInput): string {
  const base =
    q.apartmentTitle != null && q.listingId != null
      ? (questionBase[q.locale] ?? questionBaseEn)(q.apartmentTitle, String(q.listingId))
      : (questionGeneric[q.locale] ?? questionGenericEn);
  if (q.checkin && q.checkout) {
    const line = (simpleDatesLine[q.locale] ?? simpleDatesLine.en)(
      formatDMYDot(q.checkin),
      formatDMYDot(q.checkout),
      q.guests ?? 1,
    );
    return `${base}\n${line}`;
  }
  return base;
}

export function buildQuestionWhatsappUrl(phone: string, q: QuestionInput): string {
  const digits = phone.replace(/\D/g, '');
  const text = encodeURIComponent(formatQuestionMessage(q));
  return `https://wa.me/${digits}?text=${text}`;
}

export function buildQuestionTelegramUrl(telegramLink: string, q: QuestionInput): string {
  if (!telegramLink) return '';
  const text = encodeURIComponent(formatQuestionMessage(q));
  const sep = telegramLink.includes('?') ? '&' : '?';
  return `${telegramLink}${sep}text=${text}`;
}
